import React, { useCallback } from 'react';
import Table from '../../../core/form/Table';
import Button from '../../../core/form/Button';
import Row from '../../../core/layout/Row';
import PropTypes from 'prop-types';
import Async from '../../../core/layout/Async';
import {
  CONTRACT_PRODUCTS,
  HS_SKU_PRODUCTS_CAN_BE_EDITED,
  HS_SKU_PRODUCTS_CAN_BE_REMOVED_OR_ADDED_ON_EDIT,
} from '../../../core/consts';

const realCurrencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const dollarCurrencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const PostPaymentContractProductsList = (props) => {
  const handleHasRenderActions = useCallback((product) => {
    return !product.description.includes('Uso extra - ');
  }, []);

  const handleGetActionsToRender = useCallback((product) => {
    const actions = [];
    if (HS_SKU_PRODUCTS_CAN_BE_EDITED.includes(product.hs_sku)) {
      actions.push('edit');
    }
    if (HS_SKU_PRODUCTS_CAN_BE_REMOVED_OR_ADDED_ON_EDIT.includes(product.hs_sku)) {
      actions.push('delete');
    }

    return actions;
  }, []);

  return (
    <Row>
      <Async fetching={props.loading}>
        <Table
          columns={[
            {
              label: 'Product',
              path: 'description',
            },
            {
              label: 'Amount',
              path: 'amount',
              width: '150px',
            },
            {
              label: 'Free Amount',
              path: 'freeAmount',
              width: '150px',
            },
            {
              label: 'Final Unit Price',
              path: 'finalUnitPrice',
              width: '150px',
              render: (cell, row) => {
                if (
                  [CONTRACT_PRODUCTS.EXTRA_LICENSES_RANGE, CONTRACT_PRODUCTS.EXTRA_LICENSES_SEAT_RANGE].includes(
                    row.hs_sku
                  )
                ) {
                  const { min, max } = (row.finalUnitPriceRanges || []).reduce((acc, range) => {
                    if (!acc.min) acc.min = range.price;
                    if (!acc.max) acc.max = range.price;
                    if (range.price < acc.min) {
                      acc.min = range.price;
                    }
                    if (range.price > acc.max) {
                      acc.max = range.price;
                    }
                    return acc;
                  }, {});
                  return props.currencyType === 'real'
                    ? `${realCurrencyFormatter.format(min)} - ${realCurrencyFormatter.format(max)}`
                    : `${dollarCurrencyFormatter.format(min)} - ${dollarCurrencyFormatter.format(max)}`;
                }
                return props.currencyType === 'real'
                  ? realCurrencyFormatter.format(cell)
                  : dollarCurrencyFormatter.format(cell);
              },
            },
            {
              label: 'Installments',
              path: 'installments',
              width: '150px',
            },
            {
              label: 'Paid Installments',
              path: 'paidInstallments',
              width: '150px',
            },
            ...(!props.readOnly
              ? [
                  {
                    label: 'Actions',
                    width: '100px',
                    render: (cell, row) => {
                      if (handleHasRenderActions(row)) {
                        let actions = [];
                        if (props.preserveCoreProducts) {
                          actions = handleGetActionsToRender(row);
                        } else {
                          actions = ['edit', 'delete'];
                        }

                        return (
                          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                            {actions.includes('edit') && (
                              <Button
                                icon="edit"
                                disabled={props.submitting || props.disabled}
                                onClick={() => props.onEdit(row)}
                              />
                            )}
                            {actions.includes('delete') && row.description !== 'Débitos' && (
                              <Button
                                icon="delete"
                                disabled={props.submitting || props.disabled}
                                onClick={() => props.onDelete(row)}
                              />
                            )}
                          </div>
                        );
                      }

                      return null;
                    },
                  },
                ]
              : []),
          ]}
          rows={props.data}
          rowKey="rowKey"
        />
      </Async>
    </Row>
  );
};

PostPaymentContractProductsList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      amount: PropTypes.number,
      installments: PropTypes.number,
      finalUnitPrice: PropTypes.string.isRequired,
    })
  ),
  readOnly: PropTypes.bool,
  preserveCoreProducts: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  submitting: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  currencyType: PropTypes.oneOf(['real', 'dollar']),
};

PostPaymentContractProductsList.defaultProps = {
  data: [],
  submitting: false,
  readOnly: false,
  loading: false,
  currencyType: 'real',
};

export default PostPaymentContractProductsList;
